import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { UserAvatar } from '@/components/ui/Avatar'
import {
  useUpdateProfile,
  useUploadAvatar,
  useChangePassword,
  useTotpSetup,
  useTotpVerify,
  useTotpDisable,
} from '@/hooks/useProfile'
import { Camera, Save, Loader2, Lock, ShieldCheck, ShieldOff, Eye, EyeOff, X, Upload, ImagePlus, Mail, AlertCircle, CheckCircle2} from 'lucide-react'
import EmailIntegrationsTab from '@/components/EmailIntegrationsTab'
import api from '@/lib/api'
import { useSearchParams } from 'react-router-dom'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'

const COUNTRY_CODES = [
  { code: '+55', flag: '\u{1F1E7}\u{1F1F7}', label: 'Brasil' },
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}', label: 'EUA' },
  { code: '+351', flag: '\u{1F1F5}\u{1F1F9}', label: 'Portugal' },
  { code: '+44', flag: '\u{1F1EC}\u{1F1E7}', label: 'Reino Unido' },
  { code: '+34', flag: '\u{1F1EA}\u{1F1F8}', label: 'Espanha' },
]

// Regras de senha (espelham app/schemas/user.py PasswordChange.validate_password)
const passwordRules: { label: string; test: (v: string) => boolean }[] = [
  { label: 'mínimo 8 caracteres', test: (v) => v.length >= 8 },
  { label: '1 letra maiúscula',   test: (v) => /[A-Z]/.test(v) },
  { label: '1 letra minúscula',   test: (v) => /[a-z]/.test(v) },
  { label: '1 dígito',            test: (v) => /\d/.test(v) },
]

// --- Crop helper: extracts a cropped area from an image ---
function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', (e) => reject(e))
    img.setAttribute('crossOrigin', 'anonymous')
    img.src = url
  })
}

async function getCroppedImg(imageSrc: string, pixelCrop: Area): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!

  const size = Math.min(pixelCrop.width, pixelCrop.height)
  canvas.width = size
  canvas.height = size

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    size,
    size,
  )

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.92)
  })
}

// --- Avatar Upload Modal ---
interface AvatarModalProps {
  open: boolean
  onClose: () => void
  onUpload: (file: File) => Promise<void>
  userName: string
  currentAvatarSrc?: string
  isUploading: boolean
}

function AvatarUploadModal({ open, onClose, onUpload, userName, currentAvatarSrc, isUploading }: AvatarModalProps) {
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }, [])

  function handleFileSelect(file: File) {
    if (!file.type.startsWith('image/')) return
    if (file.size > 5 * 1024 * 1024) return
    const reader = new FileReader()
    reader.onload = () => {
      setImageSrc(reader.result as string)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
    }
    reader.readAsDataURL(file)
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDraggingOver(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelect(file)
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setIsDraggingOver(true)
  }

  function handleDragLeave() {
    setIsDraggingOver(false)
  }

  async function handleSave() {
    if (!imageSrc || !croppedAreaPixels) return
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels)
    const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })
    await onUpload(file)
    handleClose()
  }

  function handleClose() {
    setImageSrc(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    onClose()
  }

  function handleBack() {
    setImageSrc(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-background rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <h2 className="text-lg font-semibold">Adicionar foto</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {!imageSrc ? (
          /* Step 1: Upload / Drag & Drop */
          <div className="p-5 space-y-5">
            <p className="text-sm text-muted-foreground">
              Faca upload da sua foto para sua equipe reconhecer voce mais facilmente.
            </p>

            {/* Drop zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center cursor-pointer transition-colors py-8 ${
                isDraggingOver ? 'opacity-80' : ''
              }`}
            >
              <div className={`w-40 h-40 rounded-full flex items-center justify-center mb-4 border-2 border-dashed transition-colors ${
                isDraggingOver
                  ? 'border-primary bg-primary/10'
                  : 'border-border bg-muted/40'
              }`}>
                {currentAvatarSrc ? (
                  <img src={currentAvatarSrc} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <UserAvatar name={userName} size="lg" className="!h-36 !w-36 text-5xl" />
                )}
              </div>

              <p className="text-sm text-muted-foreground text-center">
                Arraste e solte sua foto aqui ou{' '}
                <span className="text-primary font-medium cursor-pointer hover:underline">
                  faca upload da foto
                </span>
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleInputChange}
              className="hidden"
            />

            {/* Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => fileInputRef.current?.click()}
            >
              <ImagePlus className="h-4 w-4 mr-2" /> Escolha uma foto
            </Button>
          </div>
        ) : (
          /* Step 2: Crop & Adjust */
          <div className="p-5 space-y-4">
            <p className="text-sm text-muted-foreground">
              Ajuste o enquadramento da sua foto arrastando e usando o zoom.
            </p>

            {/* Crop area */}
            <div className="relative w-full h-72 rounded-xl overflow-hidden bg-muted">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            {/* Zoom slider */}
            <div className="flex items-center gap-3 px-2">
              <span className="text-xs text-muted-foreground">-</span>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="flex-1 accent-primary h-1.5 cursor-pointer"
              />
              <span className="text-xs text-muted-foreground">+</span>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Button variant="ghost" className="flex-1" onClick={handleBack}>
                Voltar
              </Button>
              <Button
                className="flex-1"
                onClick={handleSave}
                disabled={isUploading}
              >
                {isUploading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Salvar foto
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// --- Main Page ---
export default function ProfilePage() {
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab')
  const setup2fa = searchParams.get('setup2fa') === '1'

  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error'>('success')
  const [showAvatarModal, setShowAvatarModal] = useState(false)

  // Profile form
  const [firstName, setFirstName] = useState(user?.first_name || '')
  const [lastName, setLastName] = useState(user?.last_name || '')
  const [phone, setPhone] = useState(() => {
    const p = user?.phone || ''
    const match = COUNTRY_CODES.find((c) => p.startsWith(c.code))
    return match ? p.slice(match.code.length).trim() : p.replace(/^\+/, '')
  })
  const [countryCode, setCountryCode] = useState(
    COUNTRY_CODES.find((c) => user?.phone?.startsWith(c.code))?.code || '+55'
  )
  const [company] = useState((user?.attributes?.company as string) || '')

  // Password form
  const [showPasswordForm, setShowPasswordForm] = useState(initialTab === 'security')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPw, setShowCurrentPw] = useState(false)
  const [showNewPw, setShowNewPw] = useState(false)

  // TOTP
  const [totpSetupData, setTotpSetupData] = useState<{ qr_code: string; secret: string } | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [disablePassword, setDisablePassword] = useState('')
  const [showDisableForm, setShowDisableForm] = useState(false)

  // Mutations
  const updateProfile = useUpdateProfile()
  const uploadAvatar = useUploadAvatar()
  const changePassword = useChangePassword()
  const totpSetup = useTotpSetup()
  const [show2faBanner, setShow2faBanner] = useState(setup2fa)

  // SMTP per-user (envio de propostas pelo proprio e-mail)
  const [smtpStatus, setSmtpStatus] = useState<{ configured: boolean; smtp_host?: string | null; smtp_port?: number | null; smtp_username?: string | null; smtp_from_name?: string | null } | null>(null)
  const [smtpForm, setSmtpForm] = useState({ smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_username: '', smtp_password: '', smtp_from_name: '' })
  const [smtpSaving, setSmtpSaving] = useState(false)
  const [smtpTesting, setSmtpTesting] = useState(false)
  const [smtpMsg, setSmtpMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const smtpFocusRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api.get('/v1/users/me/smtp').then(r => {
      setSmtpStatus(r.data)
      if (r.data?.configured) {
        setSmtpForm(f => ({
          ...f,
          smtp_host: r.data.smtp_host || f.smtp_host,
          smtp_port: r.data.smtp_port || 587,
          smtp_username: r.data.smtp_username || '',
          smtp_from_name: r.data.smtp_from_name || '',
        }))
      }
    }).catch(() => {})
  }, [])

  // Auto-scroll para SMTP quando ?tab=smtp
  useEffect(() => {
    if (initialTab === 'smtp' && smtpFocusRef.current) {
      smtpFocusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [initialTab, smtpStatus])

  const saveSmtp = async () => {
    setSmtpMsg(null); setSmtpSaving(true)
    try {
      const { data } = await api.put('/v1/users/me/smtp', smtpForm)
      setSmtpStatus(data)
      setSmtpMsg({ kind: 'success', text: 'SMTP salvo com sucesso. Use "Testar" pra validar.' })
      setSmtpForm(f => ({ ...f, smtp_password: '' }))
    } catch (e: any) {
      setSmtpMsg({ kind: 'error', text: e?.response?.data?.detail || 'Erro ao salvar SMTP' })
    } finally { setSmtpSaving(false) }
  }

  const testSmtp = async () => {
    setSmtpMsg(null); setSmtpTesting(true)
    try {
      const { data } = await api.post('/v1/users/me/smtp/test')
      setSmtpMsg({ kind: 'success', text: data?.message || 'E-mail de teste enviado.' })
    } catch (e: any) {
      setSmtpMsg({ kind: 'error', text: e?.response?.data?.detail || (typeof e?.response?.data === 'string' ? e.response.data : '') || 'Falha ao testar SMTP' })
    } finally { setSmtpTesting(false) }
  }

  const clearSmtp = async () => {
    if (!confirm('Remover suas credenciais SMTP? Voce nao podera mais enviar propostas por e-mail ate reconfigurar.')) return
    try {
      await api.delete('/v1/users/me/smtp')
      setSmtpStatus({ configured: false })
      setSmtpForm({ smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_username: '', smtp_password: '', smtp_from_name: '' })
      setSmtpMsg({ kind: 'success', text: 'Credenciais SMTP removidas.' })
    } catch (e: any) {
      setSmtpMsg({ kind: 'error', text: 'Erro ao remover' })
    }
  }

  // Auto-trigger TOTP setup when redirected from login with setup2fa=1
  const securityRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (setup2fa && !user?.totp_enabled) {
      // Scroll to security section and auto-trigger setup
      setTimeout(() => {
        securityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (!totpSetupData) {
          handleTotpSetup()
        }
      }, 500)
    }
  }, [])
  const totpVerify = useTotpVerify()
  const totpDisable = useTotpDisable()

  function showMsg(msg: string, type: 'success' | 'error' = 'success') {
    setMessage(msg)
    setMessageType(type)
    setTimeout(() => setMessage(''), 4000)
  }

  async function handleSaveProfile() {
    try {
      await updateProfile.mutateAsync({
        first_name: firstName,
        last_name: lastName,
        phone: `${countryCode}${phone}`,
      })
      showMsg('Perfil atualizado com sucesso!')
    } catch {
      showMsg('Erro ao atualizar perfil', 'error')
    }
  }

  async function handleAvatarUpload(file: File) {
    try {
      await uploadAvatar.mutateAsync(file)
      showMsg('Avatar atualizado!')
    } catch {
      showMsg('Erro ao enviar avatar', 'error')
    }
  }

  async function handleChangePassword() {
    if (newPassword !== confirmPassword) {
      showMsg('As senhas não conferem', 'error')
      return
    }
    const failed = passwordRules.filter((r) => !r.test(newPassword))
    if (failed.length > 0) {
      showMsg(`Senha não atende: ${failed.map((r) => r.label).join(', ')}`, 'error')
      return
    }
    try {
      await changePassword.mutateAsync({
        current_password: currentPassword,
        new_password: newPassword,
      })
      showMsg('Senha alterada com sucesso!')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setShowPasswordForm(false)
    } catch (err: any) {
      // Extrai mensagem detalhada do backend (FastAPI/Pydantic)
      const detail = err?.response?.data?.detail
      let msg = 'Erro ao alterar senha'
      if (typeof detail === 'string') {
        msg = detail
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        // Pydantic ValidationError → array de erros
        msg = detail
          .map((d: any) => (d.msg || '').replace(/^Value error,\s*/, ''))
          .filter(Boolean)
          .join(' · ')
      }
      showMsg(msg, 'error')
    }
  }

  async function handleTotpSetup() {
    try {
      const data = await totpSetup.mutateAsync()
      setTotpSetupData(data)
      setTotpCode('')
    } catch {
      showMsg('Erro ao configurar 2FA', 'error')
    }
  }

  async function handleTotpVerify() {
    if (totpCode.length !== 6) {
      showMsg('Digite o codigo de 6 digitos', 'error')
      return
    }
    try {
      await totpVerify.mutateAsync(totpCode)
      showMsg('Autenticacao em duas etapas ativada!')
      setTotpSetupData(null)
      setTotpCode('')
    } catch {
      showMsg('Codigo invalido. Tente novamente.', 'error')
    }
  }

  async function handleTotpDisable() {
    if (!disablePassword) {
      showMsg('Digite sua senha para desativar', 'error')
      return
    }
    try {
      await totpDisable.mutateAsync(disablePassword)
      showMsg('Autenticacao em duas etapas desativada.')
      setShowDisableForm(false)
      setDisablePassword('')
    } catch {
      showMsg('Senha incorreta', 'error')
    }
  }

  const avatarSrc = user?.avatar_url
    ? `https://social.myncc.com.br${user.avatar_url}`
    : undefined

  return (
    <>
      <div className="px-6 pt-6"><h1 className="text-2xl font-bold">Perfil</h1><p className="text-muted-foreground text-sm">Gerencie suas informacoes pessoais e seguranca</p></div>

      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {message && (
          <div
            className={`p-3 rounded-lg text-sm ${
              messageType === 'success'
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
            }`}
          >
            {message}
          </div>
        )}

        {/* 2FA Mandatory Banner */}
      {show2faBanner && !user?.totp_enabled && (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Ativacao de autenticacao em duas etapas obrigatoria</p>
            <p className="text-amber-600 dark:text-amber-300 mt-0.5">Seu nivel de acesso requer autenticacao em duas etapas (2FA). Configure abaixo para continuar usando o sistema.</p>
          </div>
        </div>
      )}

      {/* Profile Card */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-6">Perfil</h2>

          <div className="flex flex-col md:flex-row gap-8">
            {/* Left: Avatar */}
            <div className="flex flex-col items-center gap-3 md:w-48 shrink-0">
              <div className="relative group">
                <UserAvatar name={user?.full_name || 'User'} src={avatarSrc} size="lg" className="!h-24 !w-24 text-2xl" />
                <button
                  onClick={() => setShowAvatarModal(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <Camera className="h-6 w-6 text-white" />
                </button>
              </div>
              {uploadAvatar.isPending && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Enviando...
                </div>
              )}
              <div className="text-center">
                <p className="font-semibold">{user?.full_name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                {user?.role && (
                  <p className="text-xs text-muted-foreground mt-1">{user.role.display_name}</p>
                )}
              </div>
            </div>

            {/* Right: Form */}
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Nome</label>
                  <Input className="mt-1" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium">Sobrenome</label>
                  <Input className="mt-1" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Nome da empresa</label>
                  <Input className="mt-1" value={company} disabled />
                </div>
                <div>
                  <label className="text-sm font-medium">Numero de telefone</label>
                  <div className="flex gap-2 mt-1">
                    <select
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      className="flex h-10 rounded-md border border-input bg-background px-2 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </option>
                      ))}
                    </select>
                    <Input
                      className="flex-1"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="11996344065"
                    />
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveProfile} disabled={updateProfile.isPending}>
                {updateProfile.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Salvar Alteracoes
              </Button>
            </div>
          </div>
        </Card>

        {/* Password Card */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-2">Senha</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Proteja sua conta com uma senha forte.
          </p>

          {!showPasswordForm ? (
            <Button variant="outline" onClick={() => setShowPasswordForm(true)}>
              <Lock className="h-4 w-4 mr-2" /> Alterar senha
            </Button>
          ) : (
            <div className="max-w-md space-y-3">
              <div className="relative">
                <label className="text-sm font-medium">Senha Atual</label>
                <div className="relative mt-1">
                  <Input
                    type={showCurrentPw ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="relative">
                <label className="text-sm font-medium">Nova Senha</label>
                <div className="relative mt-1">
                  <Input
                    type={showNewPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres com maiúscula, minúscula e número"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {/* Checklist de regras em tempo real */}
                <ul className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {passwordRules.map((r) => {
                    const ok = newPassword.length > 0 && r.test(newPassword)
                    const idle = newPassword.length === 0
                    return (
                      <li key={r.label} className={`flex items-center gap-1.5 ${idle ? 'text-muted-foreground' : ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        <span className="inline-flex w-3.5 h-3.5 items-center justify-center rounded-full border text-[10px] font-bold leading-none"
                              style={{ borderColor: 'currentColor' }}>
                          {idle ? '·' : ok ? '✓' : '✗'}
                        </span>
                        {r.label}
                      </li>
                    )
                  })}
                </ul>
              </div>
              <div>
                <label className="text-sm font-medium">Confirmar Nova Senha</label>
                <Input
                  className="mt-1"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">As senhas não conferem</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleChangePassword}
                  disabled={
                    changePassword.isPending
                    || !currentPassword
                    || !newPassword
                    || newPassword !== confirmPassword
                    || passwordRules.some((r) => !r.test(newPassword))
                  }
                >
                  {changePassword.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Lock className="h-4 w-4 mr-1" />
                  )}
                  Alterar Senha
                </Button>
                <Button variant="ghost" onClick={() => setShowPasswordForm(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* 2FA Card */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-2">Autenticacao em duas etapas</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Proteja sua conta com uma etapa adicional de verificacao. Use um aplicativo de autenticacao
            como o Google Authenticator para confirmar sua identidade sempre que fizer login.
          </p>

          {user?.totp_enabled ? (
            <>
              <div className="flex items-center gap-2 mb-4 text-green-600 dark:text-green-400">
                <ShieldCheck className="h-5 w-5" />
                <span className="text-sm font-medium">Autenticacao em duas etapas esta ativada</span>
              </div>

              {!showDisableForm ? (
                <Button variant="outline" onClick={() => setShowDisableForm(true)} className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950">
                  <ShieldOff className="h-4 w-4 mr-2" /> Desativar
                </Button>
              ) : (
                <div className="max-w-sm space-y-3">
                  <div>
                    <label className="text-sm font-medium">Confirme sua senha</label>
                    <Input
                      className="mt-1"
                      type="password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="destructive"
                      onClick={handleTotpDisable}
                      disabled={totpDisable.isPending}
                    >
                      {totpDisable.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                      Confirmar Desativacao
                    </Button>
                    <Button variant="ghost" onClick={() => { setShowDisableForm(false); setDisablePassword('') }}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {!totpSetupData ? (
                <Button onClick={handleTotpSetup} disabled={totpSetup.isPending}>
                  {totpSetup.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <ShieldCheck className="h-4 w-4 mr-1" />
                  )}
                  Ativar
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-6">
                    <div className="flex flex-col items-center">
                      <p className="text-sm font-medium mb-2">Escaneie o QR code no Google Authenticator:</p>
                      <img
                        src={totpSetupData.qr_code}
                        alt="QR Code TOTP"
                        className="w-48 h-48 border rounded-lg"
                      />
                      <p className="text-xs text-muted-foreground mt-2 font-mono select-all break-all max-w-[200px] text-center">
                        {totpSetupData.secret}
                      </p>
                    </div>
                    <div className="flex-1 space-y-3">
                      <p className="text-sm text-muted-foreground">
                        Apos escanear, digite o codigo de 6 digitos exibido no app:
                      </p>
                      <Input
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="font-mono text-center text-xl tracking-widest max-w-[200px]"
                        maxLength={6}
                      />
                      <div className="flex gap-2">
                        <Button onClick={handleTotpVerify} disabled={totpVerify.isPending || totpCode.length !== 6}>
                          {totpVerify.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                          Verificar e Ativar
                        </Button>
                        <Button variant="ghost" onClick={() => setTotpSetupData(null)}>
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Integrações de E-mail (SMTP + IMAP + Assinatura) — movido das Configurações pra cá em 2026-05-15
            para que todos os usuários tenham acesso. Renderiza próprios Cards (SMTP / IMAP / Sync) que se
            alinham como grid junto aos Cards de Senha e 2FA acima via space-y-6 do container pai. */}
        <EmailIntegrationsTab />
      </div>

      {/* Avatar Upload Modal */}
      <AvatarUploadModal
        open={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onUpload={handleAvatarUpload}
        userName={user?.full_name || 'User'}
        currentAvatarSrc={avatarSrc}
        isUploading={uploadAvatar.isPending}
      />
    </>
  )
}
